import React, { useEffect, useState } from 'react';
import { papersService, ResearchPaper, CreatePaperData } from '../services/papers';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const ResearchPapers: React.FC = () => {
  const [papers, setPapers] = useState<ResearchPaper[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newPaper, setNewPaper] = useState<CreatePaperData>({
    title: '',
    authors: [],
    topic: 'ML',
    difficulty: 'Beginner',
    status: 'To Read',
  });

  useEffect(() => {
    loadPapers();
  }, []);

  const loadPapers = async () => {
    try {
      const allPapers = await papersService.getAll();
      setPapers(allPapers);
    } catch (error) {
      console.error('Error loading papers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddPaper = async () => {
    if (!newPaper.title) return;
    
    try {
      await papersService.create(newPaper);
      setNewPaper({
        title: '',
        authors: [],
        topic: 'ML',
        difficulty: 'Beginner',
        status: 'To Read',
      });
      setShowAddForm(false);
      loadPapers();
    } catch (error) {
      console.error('Error adding paper:', error);
      alert('Failed to add paper. Please try again.');
    }
  };

  const handleUpdateStatus = async (paperId: string, status: 'To Read' | 'Reading' | 'Complete') => {
    try {
      await papersService.update(paperId, { status });
      loadPapers();
    } catch (error) {
      console.error('Error updating paper:', error);
    }
  };

  const getTopicDistribution = () => {
    const topicCounts: Record<string, number> = {};
    papers.forEach(paper => {
      topicCounts[paper.topic] = (topicCounts[paper.topic] || 0) + 1;
    });
    return Object.entries(topicCounts).map(([name, value]) => ({ name, value }));
  };

  const getDifficultyDistribution = () => {
    const difficultyCounts: Record<string, number> = {};
    papers.forEach(paper => {
      difficultyCounts[paper.difficulty] = (difficultyCounts[paper.difficulty] || 0) + 1;
    });
    return Object.entries(difficultyCounts).map(([name, value]) => ({ name, value }));
  };

  const getStatusCounts = () => {
    return {
      toRead: papers.filter(p => p.status === 'To Read').length,
      reading: papers.filter(p => p.status === 'Reading').length,
      complete: papers.filter(p => p.status === 'Complete').length,
    };
  };

  const COLORS = ['#3b82f6', '#22c55e', '#a855f7', '#f59e0b', '#ef4444'];

  if (loading) {
    return <div className="p-6">Loading research papers...</div>;
  }

  const statusCounts = getStatusCounts();
  const goal = 100;
  const progress = papers.filter(p => p.status === 'Complete').length;

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            Research Paper Reading Dashboard
          </h1>
          <p className="text-gray-600">
            Goal: Read 100 papers by August 2027
          </p>
        </div>

        {/* Overall Progress */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h2 className="text-2xl font-semibold">Reading Progress</h2>
              <p className="text-gray-600">Papers read: {progress} / {goal}</p>
            </div>
            <button
              onClick={() => setShowAddForm(!showAddForm)}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
            >
              {showAddForm ? 'Cancel' : '+ Add Paper'}
            </button>
          </div>

          {/* Progress Bar */}
          <div className="w-full bg-gray-200 rounded-full h-6 mb-4">
            <div
              className="bg-accent h-6 rounded-full transition-all duration-300 flex items-center justify-end pr-2"
              style={{ width: `${Math.min((progress / goal) * 100, 100)}%` }}
            >
              <span className="text-white text-sm font-medium">
                {Math.round((progress / goal) * 100)}%
              </span>
            </div>
          </div>

          {/* Status Counts */}
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-2xl font-bold text-gray-600">{statusCounts.toRead}</p>
              <p className="text-sm text-gray-600">To Read</p>
            </div>
            <div className="text-center p-4 bg-yellow-50 rounded-lg">
              <p className="text-2xl font-bold text-yellow-600">{statusCounts.reading}</p>
              <p className="text-sm text-gray-600">Reading</p>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <p className="text-2xl font-bold text-green-600">{statusCounts.complete}</p>
              <p className="text-sm text-gray-600">Complete</p>
            </div>
          </div>

          {/* Add Paper Form */}
          {showAddForm && (
            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <h4 className="font-semibold mb-3">Add New Paper</h4>
              <div className="space-y-3">
                <input
                  type="text"
                  placeholder="Paper Title"
                  value={newPaper.title}
                  onChange={(e) => setNewPaper({ ...newPaper, title: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
                <div className="grid grid-cols-2 gap-3">
                  <select
                    value={newPaper.topic}
                    onChange={(e) => setNewPaper({ ...newPaper, topic: e.target.value as any })}
                    className="px-3 py-2 border rounded-lg"
                  >
                    <option value="ML">ML</option>
                    <option value="CV">CV</option>
                    <option value="QC">QC</option>
                    <option value="QML">QML</option>
                    <option value="Security">Security</option>
                  </select>
                  <select
                    value={newPaper.difficulty}
                    onChange={(e) => setNewPaper({ ...newPaper, difficulty: e.target.value as any })}
                    className="px-3 py-2 border rounded-lg"
                  >
                    <option value="Beginner">Beginner</option>
                    <option value="Intermediate">Intermediate</option>
                    <option value="Advanced">Advanced</option>
                  </select>
                </div>
                <input
                  type="text"
                  placeholder="arXiv ID or URL (optional)"
                  value={newPaper.arxiv_id || ''}
                  onChange={(e) => setNewPaper({ ...newPaper, arxiv_id: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
                <button
                  onClick={handleAddPaper}
                  className="w-full px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark"
                >
                  Add Paper
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Topic Distribution */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-xl font-semibold mb-4">Topic Distribution</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={getTopicDistribution()}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={(entry: any) => `${entry.name} ${(entry.percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {getTopicDistribution().map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Difficulty Distribution */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-xl font-semibold mb-4">Difficulty Distribution</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={getDifficultyDistribution()}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="value" fill="#a855f7" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Paper Lists */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* To Read */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-xl font-semibold mb-4">To Read ({statusCounts.toRead})</h3>
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {papers.filter(p => p.status === 'To Read').map((paper) => (
                <div key={paper.id} className="p-3 border rounded-lg hover:bg-gray-50">
                  <h4 className="font-semibold text-sm mb-1">{paper.title}</h4>
                  <div className="flex gap-2 mb-2">
                    <span className="text-xs px-2 py-1 bg-gray-100 rounded">{paper.topic}</span>
                    <span className="text-xs px-2 py-1 bg-gray-100 rounded">{paper.difficulty}</span>
                  </div>
                  <button
                    onClick={() => handleUpdateStatus(paper.id, 'Reading')}
                    className="text-xs text-primary hover:underline"
                  >
                    Start Reading →
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Reading */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-xl font-semibold mb-4">Reading ({statusCounts.reading})</h3>
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {papers.filter(p => p.status === 'Reading').map((paper) => (
                <div key={paper.id} className="p-3 border rounded-lg hover:bg-gray-50">
                  <h4 className="font-semibold text-sm mb-1">{paper.title}</h4>
                  <div className="flex gap-2 mb-2">
                    <span className="text-xs px-2 py-1 bg-gray-100 rounded">{paper.topic}</span>
                    <span className="text-xs px-2 py-1 bg-yellow-100 rounded">Pass {paper.pass_number}</span>
                  </div>
                  <button
                    onClick={() => handleUpdateStatus(paper.id, 'Complete')}
                    className="text-xs text-green-600 hover:underline"
                  >
                    Mark Complete →
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Complete */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-xl font-semibold mb-4">Complete ({statusCounts.complete})</h3>
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {papers.filter(p => p.status === 'Complete').slice(0, 10).map((paper) => (
                <div key={paper.id} className="p-3 border rounded-lg hover:bg-gray-50">
                  <h4 className="font-semibold text-sm mb-1">{paper.title}</h4>
                  <div className="flex gap-2 mb-1">
                    <span className="text-xs px-2 py-1 bg-gray-100 rounded">{paper.topic}</span>
                    {paper.time_spent_hours > 0 && (
                      <span className="text-xs text-gray-600">
                        {paper.time_spent_hours.toFixed(1)}h
                      </span>
                    )}
                  </div>
                  {paper.url && (
                    <a href={paper.url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline">
                      View Paper →
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResearchPapers;

